import { FC, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { getPublicPortal, IPublicPortalData } from 'components/contentspace/api'
import {
  clearPortalSession,
  getPortalAuthCookie,
  IPortalAuthData,
} from 'components/contentspace/sessionCookie'
import { GlobalPortalLoader } from 'routes/portal/components/GlobalPortalLoader'
import { testProps } from 'utils/test/qaData'

import UserMenu from '../../components/UserMenu'
import { usePortalParams, usePortalBasePath, buildCoursePath } from '../../hooks/usePortalParams'
import * as s from './PortalLanding.module.scss'

export const PortalLanding: FC = () => {
  const { companyId, portalId } = usePortalParams()
  const basePath = usePortalBasePath()
  const history = useHistory()

  const [portal, setPortal] = useState<IPublicPortalData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [portalAuth, setPortalAuth] = useState<IPortalAuthData | null>(null)

  useEffect(() => {
    if (!companyId || !portalId) {
      return
    }

    const existingAuth = getPortalAuthCookie(portalId)
    if (existingAuth) {
      setPortalAuth(existingAuth)
    }

    setLoading(true)
    getPublicPortal(companyId, portalId)
      .then((data) => {
        setPortal(data)

        if (data.startCourseId) {
          history.replace(buildCoursePath(basePath, data.startCourseId))
        }
      })
      .catch((err) => {
        setError(err?.message || 'Failed to load portal')
      })
      .finally(() => {
        setLoading(false)
      })
  }, [companyId, portalId, history, basePath])

  if (loading) {
    return <GlobalPortalLoader reason='portal-landing' visible />
  }

  if (error || !portal) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Error</h2>
        <p>{error || 'Portal not found'}</p>
      </div>
    )
  }

  const handleCourseClick = (courseId: string) => {
    history.push(buildCoursePath(basePath, courseId))
  }

  const handleLogout = () => {
    clearPortalSession(portalId!)
    window.location.reload()
  }

  return (
    <div className={s.root}>
      <div className={s.container}>
        <header className={s.header}>
          <div className={s.headerTop}>
            <h1 className={s.title} {...testProps({ el: 'typography', name: 'portalLandingName' })}>
              {portal.name}
            </h1>
            {portalAuth && (portalAuth.email || portalAuth.name) && (
              <div className={s.userInfo}>
                <UserMenu onLogout={handleLogout}>
                  <span className={s.userMenuTrigger}>
                    <span className={s.userName}>{portalAuth.name || portalAuth.email}</span>
                  </span>
                </UserMenu>
              </div>
            )}
          </div>
          {portal.description && (
            <p
              className={s.description}
              {...testProps({ el: 'typography', name: 'portalLandingDescription' })}
            >
              {portal.description}
            </p>
          )}
        </header>

        {portal.courses.length === 0 ? (
          <div className={s.empty}>
            <p>No courses available in this portal.</p>
          </div>
        ) : (
          <div className={s.grid}>
            {portal.courses.map((course) => (
              <div className={s.card} key={course.id} onClick={() => handleCourseClick(course.id)}>
                <div className={s.cardImage} />
                <div className={s.cardBody}>
                  <h3 className={s.cardTitle}>{course.name}</h3>
                  {course.description && <p className={s.cardDescription}>{course.description}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
